import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileSignature, Phone, Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { QUOTE_STATUS, QUOTE_TONE, fmtDay, fmtEuro } from "@/lib/format";
import { Btn, Empty, PageHeader, Pill, Spinner } from "@/components/rp";
import { CallDialog, QuoteFormDialog } from "@/components/admin/quoteDialogs";

const FILTERS = [["aperti", "Aperti"], ["da_richiamare", "Da richiamare"], ...Object.entries(QUOTE_STATUS), ["tutti", "Tutti"]];

const QuoteRow = ({ q, onOpen, onCall }) => (
  <div className={`rp-card p-4 sm:px-6 flex flex-wrap items-center gap-4 ${q.recall_due ? "border-[#C8BDAB] bg-[#E6DDCE]/40" : ""}`} data-testid={`quote-row-${q.id}`}>
    <button onClick={onOpen} className="flex-1 min-w-[220px] text-left" data-testid={`quote-row-open-${q.id}`}>
      <p className="text-xs font-mono text-[#757575]">{q.code}</p>
      <p className="font-medium">{q.title}</p>
      <p className="text-sm text-[#757575]">{q.client_name}{q.client_kind === "passaggio" && " · di passaggio"}</p>
    </button>
    <div className="text-right min-w-[120px]">
      <p className="font-medium">{fmtEuro(q.amount)}</p>
      {q.recall_date && <p className={`text-xs ${q.recall_due ? "text-[#DC3545] font-medium" : "text-[#757575]"}`}>Richiamo {fmtDay(q.recall_date)}</p>}
    </div>
    <Pill tone={QUOTE_TONE[q.status]}>{QUOTE_STATUS[q.status]}</Pill>
    {q.client_phone && <a href={`tel:${q.client_phone}`}><Btn variant="outline" className="h-10 px-4" data-testid={`quote-row-phone-${q.id}`}><Phone className="h-4 w-4" /><span className="hidden lg:inline">{q.client_phone}</span></Btn></a>}
    <Btn variant={q.recall_due ? "primary" : "outline"} className="h-10" onClick={onCall} data-testid={`quote-row-call-${q.id}`}>Esito telefonata</Btn>
  </div>
);

export default function AdminPreventivi() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "aperti";
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [call, setCall] = useState(null);
  const load = useCallback(() => api.get("/quotes", { params: { status, q } }).then((r) => setList(r.data)), [status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <PageHeader label="Preventivi" title="Trattative">
        <Btn onClick={() => setOpen(true)} data-testid="quotes-new-button"><Plus className="h-4 w-4" /> Nuovo preventivo</Btn>
      </PageHeader>
      <p className="text-sm text-[#757575] -mt-5 mb-6">Trattative prima del contratto: documenti, stato, richiami programmati e trasformazione in cantiere.</p>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#757575]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice, titolo o cliente" className="rp-field pl-11 rounded-full" data-testid="quotes-search-input" />
        </div>
        <select value={status} onChange={(e) => setParams({ status: e.target.value })} className="rp-field rounded-full w-auto min-w-[200px]" data-testid="quotes-status-filter">
          {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {!list ? <Spinner /> : !list.length ? <Empty icon={FileSignature} title="Nessun preventivo" data-testid="quotes-empty" /> : (
        <div className="space-y-2" data-testid="quotes-list">
          {list.map((x) => <QuoteRow key={x.id} q={x} onOpen={() => navigate(`/admin/preventivi/${x.id}`)} onCall={() => setCall(x)} />)}
        </div>
      )}
      <QuoteFormDialog open={open} onClose={() => setOpen(false)} onSaved={(x) => navigate(`/admin/preventivi/${x.id}`)} />
      {call && <CallDialog quote={call} onClose={() => setCall(null)} onSaved={load} />}
    </div>
  );
}
